// src/lib/preview-mock.ts
//
// Dev-only helper: builds a fake SessionResultDTO from minimal inputs so that
// Report11Page (14-15) and Report12Page (16-18) can render in preview routes
// without going through the full diagnostic + Supabase session flow.
//
// Used by /report/14-15/preview and /report/16-18/preview.

import { fixtureOffers } from "@/lib/report-offers-fixture";
import type { SessionResultDTO } from "@/lib/result.functions";
import type { ArchetypeCode, LanguageKey, RiasecCode } from "@/lib/yolutap/types";
import { getMaxScore } from "@/lib/format/scoring";
import type { SphereKind, SphereResult } from "@/lib/yolutap/sphere";
import subjectKeys1415Ru from "@/data/subject_keys_1415_ru.json";

const ALL_CODES: ArchetypeCode[] = ["A", "I", "S", "K", "L", "E", "P", "B"];

// Combo-ordering must match Report11Page:comboCode (`A I S K L E P B`).
const COMBO_ORDER: Record<ArchetypeCode, number> = {
  A: 0, I: 1, S: 2, K: 3, L: 4, E: 5, P: 6, B: 7,
};
function sortedComboCode(a: ArchetypeCode, b: ArchetypeCode): string {
  return COMBO_ORDER[a] <= COMBO_ORDER[b] ? `${a}${b}` : `${b}${a}`;
}

/** Pick a RIASEC code that the 14-15 subject_keys content covers for this
 *  pair (so the preview shows the combo's actual content instead of falling
 *  through to the riasec_defaults bucket — which always pointed to the
 *  "biology / psychology / literature" Social default and gave every
 *  combo, even «Строитель будущего», a humanitarian profile on page 5).
 *
 *  If no per-pair entry exists at all (content gap to be filled by the
 *  methodologist), returns null so the caller falls back to a random
 *  RIASEC and the existing default path still works. */
function pickCoveredRiasec(
  p1: ArchetypeCode,
  p2: ArchetypeCode,
  rand: () => number,
): RiasecCode | null {
  const combo = sortedComboCode(p1, p2);
  const map = (subjectKeys1415Ru as { pair_riasec_map?: Record<string, unknown> })
    .pair_riasec_map ?? {};
  const matches = Object.keys(map)
    .filter((k) => k.startsWith(`${combo}_`))
    .map((k) => k.slice(combo.length + 1) as RiasecCode);
  if (matches.length === 0) return null;
  return matches[Math.floor(rand() * matches.length)];
}

// Tiny mulberry32 RNG so a given seed produces a stable mock report.
function rng(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Сфера для превью и автотеста: вид и буквы задаются явно. */
export function mockSphere(kind: SphereKind, letters: RiasecCode[]): SphereResult {
  const zero = { R: 0, I: 0, A: 0, S: 0, E: 0, H: 0 } as Record<RiasecCode, number>;
  const counts = { ...zero };
  letters.forEach((l, i) => (counts[l] = 3 - i));
  return { kind, letters, counts, slots: { R: 6, I: 6, A: 6, S: 6, E: 6, H: 6 }, share: { ...zero } };
}

/** Варианты смысла в превью: ?sphere=one:H | two:S,H | broad, ?lead_tie=1, ?close=K. */
export type MeaningPreview = { sphere?: SphereResult; leadTie?: boolean; close?: ArchetypeCode[] };

export function parseMeaningSearch(search: Record<string, unknown>): MeaningPreview | undefined {
  const out: MeaningPreview = {};
  const sp = typeof search.sphere === "string" ? search.sphere : "";
  const [kind, list = ""] = sp.split(":");
  const letters = list.split(",").filter((l) => ["R", "I", "A", "S", "E", "H"].includes(l)) as RiasecCode[];
  if (kind === "broad") out.sphere = mockSphere("broad", []);
  else if (kind === "one" && letters.length >= 1) out.sphere = mockSphere("one", letters.slice(0, 1));
  else if (kind === "two" && letters.length >= 2) out.sphere = mockSphere("two", letters.slice(0, 2));
  if (search.lead_tie === "1" || search.lead_tie === 1 || search.lead_tie === true) out.leadTie = true;
  if (typeof search.close === "string" && ALL_CODES.includes(search.close as ArchetypeCode)) {
    out.close = [search.close as ArchetypeCode];
  }
  return Object.keys(out).length ? out : undefined;
}

export function meaningOverrides(m: MeaningPreview | undefined) {
  return {
    sphereOverride: m?.sphere,
    tieLeadOverride: m?.leadTie,
    alsoCloseOverride: m?.close,
  };
}

export function buildMockSessionResult({
  seed,
  name,
  age,
  lang,
  ageGroup,
  primaryCode,
  secondaryCode,
  packageType = "basic",
  // PREVIEW-MOCK-ONLY overrides for B-wire verification. Real session
  // rendering reads mixed_profile / tie_group straight from
  // calculateResult — these params do NOT affect that path.
  mixedProfileOverride,
  tieGroupOverride,
  // Force a specific dominant_riasec letter. Preview-only. Real
  // sessions derive this from calculateResult → calculateRiasecScores
  // → getDominantRiasec. Used by the fixture generator to shoot a
  // guaranteed-primary letter (e.g. H for 14-15 QA) without needing
  // to author matching answers.
  dominantRiasecOverride,
  // «Профиль пока не ярко выражен» (src/lib/weak-profile.ts): лидер на
  // пороге 0.375·M, второй на балл ниже, остальные ещё ниже. Для автотеста
  // отчёта (A2, E) и превью. Ведущий остаётся ведущим — отрыв в один балл.
  weakOverride,
  // Блоки рекомендаций (часть C) из фикстуры — для превью и автотеста.
  offers,
  // Смысл результатов (13.10): сфера «одна / две / широкие», ничья ведущего
  // и поддерживающего, «так же близко». Для автотеста вариантов показа.
  sphereOverride,
  tieLeadOverride,
  alsoCloseOverride,
}: {
  seed: number;
  name: string;
  age: number;
  lang: LanguageKey;
  ageGroup: string;
  primaryCode?: ArchetypeCode;
  secondaryCode?: ArchetypeCode;
  packageType?: "basic" | "plus" | "premium";
  mixedProfileOverride?: boolean;
  tieGroupOverride?: ArchetypeCode[];
  dominantRiasecOverride?: RiasecCode;
  weakOverride?: boolean;
  offers?: boolean;
  sphereOverride?: SphereResult | null;
  tieLeadOverride?: boolean;
  alsoCloseOverride?: ArchetypeCode[];
}): SessionResultDTO {
  const rand = rng(seed);

  // Pick two distinct archetypes either from params or random
  const shuffled = [...ALL_CODES].sort(() => rand() - 0.5);
  const p1 = primaryCode ?? shuffled[0];
  const p2 =
    secondaryCode && secondaryCode !== p1
      ? secondaryCode
      : (shuffled.find((c) => c !== p1) ?? shuffled[1]);

  // Build scores anchored to the band's actual cap (was hardcoded
  // 17..20 / 13..16 which silently broke the moment a band's cap moved
  // — e.g. 14-15 dropped 19 → 16 in Plan C and the preview kept
  // rendering primary=18 against an «of 16» label). Compute the
  // window from getMaxScore so any future cap move auto-propagates
  // through the preview path the same way it propagates through
  // getScoreOfLabel and barWidthPct in the renderer.
  const cap = getMaxScore(ageGroup);
  const scores: Record<string, number> = {};
  const others = ALL_CODES.filter((c) => c !== p1 && c !== p2);
  // Primary lands in the top-3 below cap (cap−2 .. cap), secondary in
  // the next band (cap*0.6 .. cap*0.8), others trail off.
  scores[p1] = Math.max(0, Math.min(cap, cap - Math.floor(rand() * 3)));
  scores[p2] = Math.max(
    0,
    Math.min(cap - 1, Math.floor(cap * 0.6 + rand() * cap * 0.2)),
  );
  others.forEach((c, i) => {
    const base = Math.floor(cap * 0.2 + rand() * cap * 0.3) - i;
    scores[c] = Math.max(0, Math.min(cap, base));
  });
  if (weakOverride) {
    const lead = Math.floor(0.375 * cap);
    scores[p1] = lead;
    scores[p2] = lead - 1;
    others.forEach((c, i) => (scores[c] = Math.max(0, lead - 2 - (i % 3))));
  }
  // Ничьи для вариантов показа: баллы делаются равными, как в живом расчёте.
  if (tieLeadOverride) scores[p2] = scores[p1];
  for (const c of alsoCloseOverride ?? []) if (c !== p1 && c !== p2) scores[c] = scores[p2];

  // RIASEC dominant — instead of a uniform random pick (which with a
  // fixed seed always lands the same code regardless of the requested
  // combo, sending every preview through the riasec_defaults Social
  // fallback), prefer a RIASEC that the 14-15 subject_keys data
  // actually covers for THIS pair. That way the preview demonstrates
  // the combo's archetype-specific page-5 content instead of always
  // showing "biology / psychology / literature".
  const RIASEC_CODES: RiasecCode[] = ["R", "I", "A", "S", "E", "H"];
  const covered = pickCoveredRiasec(p1 as ArchetypeCode, p2 as ArchetypeCode, rand);
  const dominantRiasec =
    dominantRiasecOverride ??
    covered ??
    RIASEC_CODES[Math.floor(rand() * RIASEC_CODES.length)];

  return {
    child_name: name,
    language_key: lang,
    status: "complete",
    package_type: packageType,
    result: {
      age_group: ageGroup,
      test_version_id: "preview_mock_v1",
      report_template_version_id: "preview_mock_v1",
      language_key: lang,
      child_scores: scores as never,
      total_score: Object.values(scores).reduce((a, b) => a + b, 0),
      primary_archetype: p1,
      secondary_archetype: p2,
      mixed_profile: mixedProfileOverride ?? false,
      tie_group: (tieGroupOverride ?? [p1, p2]) as ArchetypeCode[],
      combination_key: `${p1}_${p2}`,
      lowest_archetypes: [others[others.length - 1]] as ArchetypeCode[],
      status: "complete",
      riasec_scores: {
        R: Math.floor(rand() * 6),
        I: Math.floor(rand() * 6),
        A: Math.floor(rand() * 6),
        S: Math.floor(rand() * 6),
        E: Math.floor(rand() * 6),
        H: Math.floor(rand() * 6),
      } as Record<RiasecCode, number>,
      dominant_riasec: dominantRiasec,
      // Сфера для страниц 14-15 / 16-18: по умолчанию одна — та же буква.
      sphere:
        sphereOverride === null
          ? undefined
          : (sphereOverride ?? mockSphere("one", [dominantRiasec])),
      tie_lead: tieLeadOverride ?? false,
      also_close: alsoCloseOverride ?? [],
      work_format: {
        hire_vs_startup: rand() > 0.5 ? "hire" : "startup",
        sector: rand() > 0.5 ? "private" : "public",
        scale: rand() > 0.5 ? "large" : "small",
        geography: rand() > 0.5 ? "global" : "local",
      },
    },
    offers: offers ? fixtureOffers(ageGroup, p1) : undefined,
    child_summary: "",
    primary: {
      code: p1,
      name: p1,
      summary: "",
      manifestation: [],
      strengths: [],
      growth_areas: [],
      score: scores[p1],
      rank: 1,
    },
    secondary: {
      code: p2,
      name: p2,
      summary: "",
      manifestation: [],
      strengths: [],
      growth_areas: [],
      score: scores[p2],
      rank: 2,
    },
    combination: null,
    alignment: [],
    lowest: [],
    cta: "",
  };
}
